import { ILicensePlateInfoForm, IManualLicensePlateInfoForm, IVerifyYourIdentity, IVerifyYourInformation } from 'types/form';
import { Address } from 'types/proveIdentity';
import { IPostPlateToVINResponse } from './postPlateToVIN';
import {
  ApiResources,
  createErrorResponse,
  formatPostalCode,
  getRequestHeaders,
  getUrl,
  Methods,
  processResponse,
} from './utils';

interface ICreateContactAddress {
  isCurrent: boolean;
  monthsAtAddress?: number;
  streetLine1: string;
  streetLine2: string;
  city: string;
  state: string;
  zip: string;
}
interface IMetaData {
  version: string;
  loanApplicationId: string;
  partnerName: string;
  secondaryLeadSource: string;
  utm: {
    utm_medium: string;
    utm_campaign: string;
    utm_content: string;
    secondary_source: string;
    gclid: string;
    subid: string;
    clickref: string;
    search_id: string;
    oneToOneConsent: string;
    subid1: string;
    subid2: string;
    subid3: string;
    sharedid: string;
    clickid: string;
    arm_enabled: string;
    utm_source: string;
    irclickid: string;
  };
}
export interface ICreateContactPayload {
  contactId?: string | null;
  leadId?: string | null;
  assetId?: string | null;
  contactRequest: {
    firstName: string;
    lastName: string;
    email: string;
    cellPhone: string;
    isConsentSelected: boolean;
    dateOfBirth: string;
    ssn: string;
    residencyStatusType: string;
    addresses: ICreateContactAddress[];
  };
  assetRequest: {
    plateState: string;
    plateNumber: string;
    estimatedMileageRange: string;
    vin: string;
    year: string;
    make: string;
    model: string;
  };
  metaData: IMetaData;
  correlationId : string | null
}
interface ErrorResponse {
  error: {
    status?: number | string;
    message: string;
  };
}
export interface ICreateContactResponse {
  contactId: string;
  leadId: string;
  assetId: string;
  cellPhone: string;
  email: string;
  timestamp?: string;
  activeLeads: [
    {
      leadId: string;
      leadType: string;
    },
  ];
}

const mapAddressToCreateContactAddress = (verifyYourInfoAddresses: Address[]): ICreateContactAddress[] => {
  return verifyYourInfoAddresses.map((address, index) => {
    return {
      isCurrent: index === 0, //First address in the array is the current address
      streetLine1: address?.address ?? '',
      streetLine2: address?.extendedAddress ?? '',
      city: address?.city ?? '',
      state: address?.region ?? '',
      zip: formatPostalCode(address.postalCode) ?? '',
    };
  });
};

export const postContact = async (
  licensePlateInfoForm: ILicensePlateInfoForm,
  manualLicensePlateInfoForm: IManualLicensePlateInfoForm,
  verifyYourInformation: IVerifyYourInformation,
  verifyYourIdentity: IVerifyYourIdentity,
  licensePlateInfoResponse: IPostPlateToVINResponse | null,
  metaData: IMetaData,
  correlationId : string | null
): Promise<ICreateContactResponse | ErrorResponse> => {
  const requestPayload: ICreateContactPayload = {
    contactRequest: {
      firstName: verifyYourInformation.firstName,
      lastName: verifyYourInformation.lastName,
      email: verifyYourInformation.email ?? '',
      cellPhone: verifyYourIdentity.phone,
      isConsentSelected: true,
      dateOfBirth: verifyYourInformation.dateOfBirth,
      ssn: verifyYourInformation.ssn,
      residencyStatusType: verifyYourInformation?.residency,
      addresses: mapAddressToCreateContactAddress(verifyYourInformation?.addresses ?? []),
    },
    assetRequest: {
      plateState: licensePlateInfoForm.plateState,
      plateNumber: licensePlateInfoForm.plateNumber,
      estimatedMileageRange: licensePlateInfoForm.estimatedMileageRange || manualLicensePlateInfoForm.estimatedMileage,
      vin: licensePlateInfoResponse?.vin ?? '',
      year: manualLicensePlateInfoForm.year,
      make: manualLicensePlateInfoForm.makeName || manualLicensePlateInfoForm.make,
      model: manualLicensePlateInfoForm.model,
    },
    metaData: metaData,
    correlationId: correlationId
  };
  const endpoint = `${getUrl(ApiResources.createContact)}`;
  const request = {
    method: Methods.post,
    headers: await getRequestHeaders(),
    body: JSON.stringify(requestPayload),
  };
  try {
    const response = await fetch(endpoint, request);
    return processResponse(response);
  } catch (error) {
    return createErrorResponse(error instanceof Error ? error.name : 400, error instanceof Error ? error.message : '');
  }
};


export const putContact = async (
  contactId: string | null,
  leadId: string | null,
  assetId: string | null,
  licensePlateInfoForm: ILicensePlateInfoForm,
  manualLicensePlateInfoForm: IManualLicensePlateInfoForm,
  verifyYourInformation: IVerifyYourInformation,
  verifyYourIdentity: IVerifyYourIdentity,
  licensePlateInfoResponse: IPostPlateToVINResponse | null,
  metaData: IMetaData,
  correlationId : string | null
): Promise<ICreateContactResponse | ErrorResponse> => {
  const requestPayload: ICreateContactPayload = {
    contactId: contactId,
    leadId: leadId,
    assetId: assetId,
    contactRequest: {
      firstName: verifyYourInformation.firstName,
      lastName: verifyYourInformation.lastName,
      email: verifyYourInformation.email,
      cellPhone: verifyYourIdentity.phone,
      isConsentSelected: true,
      dateOfBirth: verifyYourInformation.dateOfBirth,
      ssn: verifyYourInformation.ssn,
      residencyStatusType: verifyYourInformation?.residency,
      addresses: mapAddressToCreateContactAddress(verifyYourInformation?.addresses ?? []),
    },
    assetRequest: {
      plateState: licensePlateInfoForm.plateState,
      plateNumber: licensePlateInfoForm.plateNumber,
      estimatedMileageRange: licensePlateInfoForm.estimatedMileageRange || manualLicensePlateInfoForm.estimatedMileage,
      vin: licensePlateInfoResponse?.vin ?? '',
      year: manualLicensePlateInfoForm.year,
      make: manualLicensePlateInfoForm.makeName || manualLicensePlateInfoForm.make,
      model: manualLicensePlateInfoForm.model,
    },
    metaData: metaData,
    correlationId:correlationId
  };
  const endpoint = `${getUrl(ApiResources.updateContact)}`;
  const request = {
    method: Methods.put,
    headers: await getRequestHeaders(),
    body: JSON.stringify(requestPayload),
  };
  try {
    const response = await fetch(endpoint, request);
    return processResponse(response);
  } catch (error) {
    return createErrorResponse(error instanceof Error ? error.name : 400, error instanceof Error ? error.message : '');
  }
};

export const createContact = async (
  contactId: string | null,
  leadId: string | null,
  assetId: string | null,
  licensePlateInfoForm: ILicensePlateInfoForm,
  manualLicensePlateInfoForm: IManualLicensePlateInfoForm,
  verifyYourInformation: IVerifyYourInformation,
  verifyYourIdentity: IVerifyYourIdentity,
  licensePlateInfoResponse: IPostPlateToVINResponse | null,
  metaData: IMetaData,
  correlationId : string | null
): Promise<ICreateContactResponse | ErrorResponse> => {
  if (contactId) {
    return putContact(
      contactId,
      leadId,
      assetId,
      licensePlateInfoForm,
      manualLicensePlateInfoForm,
      verifyYourInformation,
      verifyYourIdentity,
      licensePlateInfoResponse,
      metaData,
      correlationId
    );
  }
  return postContact(
    licensePlateInfoForm,
    manualLicensePlateInfoForm,
    verifyYourInformation,
    verifyYourIdentity,
    licensePlateInfoResponse,
    metaData,
    correlationId
  );
};